import { type ChangeEvent, type FormEvent, useState } from 'react';
import { useApi, useConsulta } from './api';
import { useSesion } from './sesion';

interface Cuenta {
  nombre: string;
  apellidos: string;
  correo: string;
  telefono: string | null;
  zonaHoraria: string;
  foto: string | null;
  google: boolean;
}
interface SesionAbierta {
  id: string;
  ip: string;
  inicio: string;
  ultimoAcceso: string;
  aplicaciones: string[];
}

const ZONAS = [
  'America/Mexico_City',
  'America/Monterrey',
  'America/Tijuana',
  'America/Hermosillo',
  'America/Chihuahua',
  'America/Mazatlan',
  'America/Cancun',
];

/** Reduce la imagen a 256 px y la entrega en JPEG base64 (fotos de perfil y logos). */
export async function imagenPequena(
  archivo: File,
  lado = 256,
): Promise<{ tipo: 'image/jpeg'; base64: string }> {
  const bitmap = await createImageBitmap(archivo);
  const escala = Math.min(1, lado / Math.max(bitmap.width, bitmap.height));
  const lienzo = document.createElement('canvas');
  lienzo.width = Math.round(bitmap.width * escala);
  lienzo.height = Math.round(bitmap.height * escala);
  lienzo.getContext('2d')?.drawImage(bitmap, 0, 0, lienzo.width, lienzo.height);
  const url = lienzo.toDataURL('image/jpeg', 0.85);
  return { tipo: 'image/jpeg', base64: url.slice(url.indexOf(',') + 1) };
}

const fechaHora = (iso: string) =>
  new Intl.DateTimeFormat('es-MX', {
    dateStyle: 'medium',
    timeStyle: 'short',
    timeZone: 'America/Mexico_City',
  }).format(new Date(iso));

/**
 * Mi cuenta (arquitectura 2.12), igual en Admin, Portal, Soporte y Super Admin: foto, nombre,
 * teléfono, zona horaria, contraseña (en Keycloak), Google vinculado y sesiones abiertas.
 */
export function MiCuenta() {
  const api = useApi();
  const { cambiarClave, salir } = useSesion();
  const cuenta = useConsulta<Cuenta>('/platform/mi-cuenta');
  const sesiones = useConsulta<SesionAbierta[]>('/platform/mi-cuenta/sesiones');
  const [error, setError] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const hacer = async (fn: () => Promise<unknown>, mensaje: string) => {
    setError(null);
    setAviso(null);
    try {
      await fn();
      setAviso(mensaje);
      cuenta.recargar();
    } catch (e) {
      setError((e as Error).message);
    }
  };
  const guardar = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const d = new FormData(e.currentTarget);
    void hacer(
      () =>
        api('PUT', '/platform/mi-cuenta', {
          nombre: String(d.get('nombre') ?? ''),
          apellidos: String(d.get('apellidos') ?? ''),
          telefono: String(d.get('telefono') ?? '').trim() || null,
          zonaHoraria: String(d.get('zonaHoraria') ?? 'America/Mexico_City'),
        }),
      'Guardado',
    );
  };
  const subirFoto = (e: ChangeEvent<HTMLInputElement>) => {
    const archivo = e.target.files?.[0];
    if (!archivo) return;
    void hacer(
      async () => api('PUT', '/platform/mi-cuenta/foto', await imagenPequena(archivo)),
      'Foto actualizada',
    );
  };
  const c = cuenta.datos;
  return (
    <>
      <div className="encabezado">
        <div>
          <h1>Mi cuenta</h1>
          <div className="subtitulo">{c?.correo}</div>
        </div>
      </div>
      {cuenta.error && <p className="error">{cuenta.error}</p>}
      {error && <p className="error">{error}</p>}
      {aviso && <p className="etiqueta exito">{aviso}</p>}
      {c && (
        <>
          <section
            className="seccion cuerpo"
            style={{ display: 'flex', gap: 16, alignItems: 'center' }}
          >
            {c.foto ? (
              <img
                src={c.foto}
                alt=""
                style={{ width: 72, height: 72, borderRadius: '50%', objectFit: 'cover' }}
              />
            ) : (
              <div className="avatar" style={{ width: 72, height: 72, fontSize: 26 }}>
                {(c.nombre[0] ?? '·').toUpperCase()}
              </div>
            )}
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <label className="boton">
                Cambiar foto
                <input type="file" accept="image/*" hidden onChange={subirFoto} />
              </label>
              {c.foto && (
                <button
                  className="boton"
                  onClick={() =>
                    void hacer(() => api('DELETE', '/platform/mi-cuenta/foto'), 'Foto quitada')
                  }
                >
                  Quitar foto
                </button>
              )}
            </div>
          </section>
          <form
            className="seccion cuerpo formulario"
            onSubmit={guardar}
            key={`${c.nombre}${c.zonaHoraria}`}
          >
            <h2>Datos personales</h2>
            <label className="campo">
              <span>Nombre</span>
              <input name="nombre" defaultValue={c.nombre} required maxLength={80} />
            </label>
            <label className="campo">
              <span>Apellidos</span>
              <input name="apellidos" defaultValue={c.apellidos} required maxLength={80} />
            </label>
            <label className="campo">
              <span>Teléfono</span>
              <input
                name="telefono"
                defaultValue={c.telefono ?? ''}
                maxLength={30}
                inputMode="tel"
              />
            </label>
            <label className="campo">
              <span>Zona horaria</span>
              <select name="zonaHoraria" defaultValue={c.zonaHoraria}>
                {[...new Set([c.zonaHoraria, ...ZONAS])].map((z) => (
                  <option key={z} value={z}>
                    {z.replace('America/', '').replace('_', ' ')}
                  </option>
                ))}
              </select>
            </label>
            <div>
              <button className="boton primario">Guardar</button>
            </div>
          </form>
          <section className="seccion cuerpo">
            <h2>Seguridad</h2>
            <p className="subtitulo">
              La contraseña se cambia en la página segura de inicio de sesión: nunca pasa por la
              plataforma.
            </p>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button className="boton" onClick={() => void cambiarClave()}>
                Cambiar contraseña
              </button>
              {c.google ? (
                <button
                  className="boton"
                  onClick={() =>
                    window.confirm(
                      '¿Desvincular tu cuenta de Google? Seguirás entrando con tu correo y contraseña.',
                    ) &&
                    void hacer(
                      () => api('DELETE', '/platform/mi-cuenta/google'),
                      'Google desvinculado',
                    )
                  }
                >
                  Desvincular Google
                </button>
              ) : (
                <span className="subtitulo">
                  Google: se vincula solo la próxima vez que entres con «Entrar con Google» con este
                  mismo correo.
                </span>
              )}
            </div>
          </section>
          <section className="seccion">
            <header>
              <h2>Sesiones abiertas</h2>
              <div style={{ flex: 1 }} />
              <button
                className="boton"
                onClick={() =>
                  window.confirm(
                    'Se cerrará tu sesión en todos los dispositivos, también en este.',
                  ) && void api('POST', '/platform/mi-cuenta/sesiones/cerrar').then(() => salir())
                }
              >
                Cerrar todas
              </button>
            </header>
            <table>
              <tbody>
                {(sesiones.datos ?? []).map((s) => (
                  <tr key={s.id}>
                    <td>{s.ip}</td>
                    <td>{s.aplicaciones.join(', ')}</td>
                    <td className="subtitulo">desde {fechaHora(s.inicio)}</td>
                    <td className="subtitulo">último uso {fechaHora(s.ultimoAcceso)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        </>
      )}
    </>
  );
}
